import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllInfluencerAccountPosts.graphql';
import { Query, QueryallInfluencerAccountPostsArgs } from '../../__generated__/globalTypes';

export const useAllInfluencerAccountPostsQuery = (
  options?: QueryHookOptions<Query, QueryallInfluencerAccountPostsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallInfluencerAccountPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllInfluencerAccountPostsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallInfluencerAccountPostsArgs>
): QueryResult<Query, QueryallInfluencerAccountPostsArgs> & {
  getAllInfluencerAccountPosts: LazyQueryExecFunction<Query, QueryallInfluencerAccountPostsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllInfluencerAccountPosts, result] = useLazyQuery<Query, QueryallInfluencerAccountPostsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllInfluencerAccountPosts, ...result };
};

export const useAllInfluencerAccountPostsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllInfluencerAccountPosts = useCallback(
    (options?: Omit<QueryOptions<QueryallInfluencerAccountPostsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallInfluencerAccountPostsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllInfluencerAccountPosts };
};
// Suspense Query Hooks
export const useAllInfluencerAccountPostsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallInfluencerAccountPostsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallInfluencerAccountPostsArgs>(QUERY, {
    ...options
  });

  return result;
};
